"use client";

import type { ChatPlayerPartSnapshot, ChatPlayerRole } from "./chat-player-types";
import type { ReactNode } from "react";

import IconMaintenance from "@ui/central-icons/icons/IconMaintenance";
import { clsx } from "clsx";
import { AnimatePresence } from "motion/react";
import * as m from "motion/react-m";

import { CHAT_PLAYER_TOOL_STATUS_TRANSITION_MS } from "./chat-player-playback";

export type ChatPlayerPartRegistry = Partial<{
  [Type in ChatPlayerPartSnapshot["type"]]: (props: {
    part: Extract<ChatPlayerPartSnapshot, { type: Type }>;
    reducedMotion: boolean;
    role: ChatPlayerRole;
  }) => ReactNode;
}>;

type ChatPlayerPartRenderer = (props: {
  part: ChatPlayerPartSnapshot;
  reducedMotion: boolean;
  role: ChatPlayerRole;
}) => ReactNode;

type ChatPlayerCustomPartData = {
  title?: string;
  amount?: string;
  detail?: string;
};

type ChatPlayerToolStatus = Extract<ChatPlayerPartSnapshot, { type: "tool" }>["status"];

const TOOL_TEXT_CLASS_NAME = "tw-text-black";
const TOOL_TEXT_SHIMMER_CLASS_NAME = "shimmer-text";
const TOOL_TEXT_BLUR = "0.125rem";
const TOOL_TEXT_TRANSITION = { duration: CHAT_PLAYER_TOOL_STATUS_TRANSITION_MS / 2000, ease: "easeOut" } as const;

export function ChatPlayerPartContent({
  part,
  partRegistry,
  reducedMotion,
  role,
}: {
  part: ChatPlayerPartSnapshot;
  partRegistry?: ChatPlayerPartRegistry;
  reducedMotion: boolean;
  role: ChatPlayerRole;
}) {
  const renderPart = partRegistry?.[part.type] as ChatPlayerPartRenderer | undefined;

  if (renderPart) {
    return renderPart({ part, reducedMotion, role });
  }

  switch (part.type) {
    case "text":
      return <TextPartView content={part.content} />;
    case "speech":
      return <TimedTextPartView part={part} reducedMotion={reducedMotion} />;
    case "tool":
      return <ToolPartView part={part} reducedMotion={reducedMotion} />;
    case "custom":
      return <CustomPartView part={part} />;
  }
}

function TextPartView({ content }: { content: string }) {
  return <p className='tw-m-0 tw-whitespace-pre-wrap tw-break-words'>{content}</p>;
}

function TimedTextPartView({
  part,
  reducedMotion,
}: {
  part: Extract<ChatPlayerPartSnapshot, { type: "speech" }>;
  reducedMotion: boolean;
}) {
  if (reducedMotion) {
    return <p className='tw-m-0 tw-whitespace-pre-wrap tw-break-words'>{part.words.map(word => word.text).join("")}</p>;
  }

  return (
    <p className='tw-m-0 tw-whitespace-pre-wrap tw-break-words'>
      {part.words.map(word => (
        <span
          key={word.id}
          aria-hidden={!word.isVisible}
          className={clsx(
            "tw-transition-opacity tw-duration-200 tw-ease-out",
            word.isVisible ? "tw-opacity-100" : "tw-opacity-0",
          )}
        >
          {word.text}
        </span>
      ))}
      {part.words.length === 0 ? (
        <span
          aria-hidden='true'
          className='tw-inline-block tw-h-[1.1em] tw-w-1.5 tw-rounded-full tw-bg-current tw-opacity-25'
        />
      ) : null}
    </p>
  );
}

function ToolPartView({
  part,
  reducedMotion,
}: {
  part: Extract<ChatPlayerPartSnapshot, { type: "tool" }>;
  reducedMotion: boolean;
}) {
  const isLoading = part.status === "loading";

  return (
    <div className='tw-flex tw-w-full tw-min-w-0 tw-items-center tw-overflow-hidden tw-pr-5' aria-busy={isLoading}>
      <span aria-hidden='true' className='tw-flex tw-size-10 tw-flex-none tw-items-center tw-justify-center'>
        <span className='tw-flex tw-size-6 tw-items-center tw-justify-center tw-rounded-full tw-bg-[#EBE8E4] tw-text-[#8d8b89]'>
          <IconMaintenance className='tw-size-3 tw-fill-current' />
        </span>
      </span>
      <ToolStatusText part={part} reducedMotion={reducedMotion} />
    </div>
  );
}

function ToolStatusText({
  part,
  reducedMotion,
}: {
  part: Extract<ChatPlayerPartSnapshot, { type: "tool" }>;
  reducedMotion: boolean;
}) {
  const isLoading = part.status === "loading";
  const terminalText = getToolTerminalText(part);

  return (
    <span className='tw-block tw-min-w-0 tw-flex-1 tw-truncate tw-text-[0.875rem]/5' aria-live='polite'>
      <AnimatePresence initial={false} mode='wait'>
        <m.span
          key={part.status}
          className={clsx(
            "tw-block tw-w-full tw-min-w-0 tw-truncate",
            isLoading && (reducedMotion ? TOOL_TEXT_CLASS_NAME : TOOL_TEXT_SHIMMER_CLASS_NAME),
            !isLoading && getToolTerminalTextClassName(part.status),
          )}
          {...getToolTextMotionProps(reducedMotion)}
        >
          {isLoading ? part.data.loadingText : terminalText}
        </m.span>
      </AnimatePresence>
    </span>
  );
}

function getToolTextMotionProps(reducedMotion: boolean) {
  if (reducedMotion) {
    return {
      initial: false as const,
      transition: { duration: 0 },
    };
  }

  return {
    initial: getToolTextHiddenState(),
    animate: getToolTextVisibleState(),
    exit: getToolTextHiddenState(),
    transition: TOOL_TEXT_TRANSITION,
  };
}

function getToolTextHiddenState() {
  return { filter: `blur(${TOOL_TEXT_BLUR})`, opacity: 0 };
}

function getToolTextVisibleState() {
  return { filter: "blur(0rem)", opacity: 1 };
}

function getToolTerminalText(part: Extract<ChatPlayerPartSnapshot, { type: "tool" }>) {
  if (part.status === "error") {
    return part.data.errorText ?? part.data.successText;
  }

  return part.data.successText;
}

function getToolTerminalTextClassName(status: ChatPlayerToolStatus) {
  switch (status) {
    case "error":
      return "tw-text-red-600";
    case "loading":
    case "success":
      return TOOL_TEXT_CLASS_NAME;
  }
}

function CustomPartView({ part }: { part: Extract<ChatPlayerPartSnapshot, { type: "custom" }> }) {
  const data = getCustomPartData(part.data);

  return (
    <div className='tw-grid tw-max-w-72 tw-gap-1'>
      <span className='tw-type-xs tw-font-semibold tw-uppercase tw-opacity-70'>{data.title ?? part.renderKey}</span>
      {data.amount ? <strong className='tw-type-base tw-font-semibold'>{data.amount}</strong> : null}
      {data.detail ? <span className='tw-type-xs tw-opacity-70'>{data.detail}</span> : null}
    </div>
  );
}

function getCustomPartData(data: unknown) {
  if (typeof data !== "object" || data === null) {
    return {} satisfies ChatPlayerCustomPartData;
  }

  const record = data as Record<string, unknown>;

  return {
    title: typeof record.title === "string" ? record.title : undefined,
    amount: typeof record.amount === "string" ? record.amount : undefined,
    detail: typeof record.detail === "string" ? record.detail : undefined,
  } satisfies ChatPlayerCustomPartData;
}
